import React, { useState, useEffect } from "react";
import { makeStyles } from '@material-ui/core';
import { getUserFactoryId, getStructureTree } from '../../../index';
import { getStandard, getTraining, getStructure } from './actions';
import Chart from "react-google-charts";
import { TableContainer, TableBody, TableCell, Table, TableRow, Grid,
    TableHead, Paper, Snackbar, TextField, Typography, IconButton, Collapse,
    Box, Select, MenuItem } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import ptLocale from "date-fns/locale/pt";
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

const useStyles = makeStyles({
    table: {
        minWidth: 650,
    },
});

function HeadComponent(props) {

    const { heads } = props;

    return (
        <TableHead>
            <TableRow>
                {heads.map(h => (
                    <TableCell key={h}> {h} </TableCell>
                ))}
            </TableRow>
        </TableHead>
    )
}

function RowComponent(props) {

    const { row, heads } = props;

    const setOpen = (row) => { props.setOpen(row) }

    return (
        <React.Fragment>
            <TableRow>

                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(row)}>
                        {row.open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>

                {row.cells.map((cell, index) => (
                    <TableCell key={index}> 
                        {cell}
                    </TableCell>
                ))}

            </TableRow>

            <MiniTableComponent row={row} heads={heads} setOpen={setOpen} />
        </React.Fragment>
    );
}

function MiniTableComponent(props) {

    const { row, heads } = props;

    const setOpen = (row) => { props.setOpen(row) }

    return (
        <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={heads.length}>
                <Collapse in={row.open} timeout="auto" unmountOnExit>
                    <Box margin={1}>
                        <Table size="small" aria-label="purchases">
                            <HeadComponent heads={heads} />
                            <TableBody>
                                {row.nodes.map(node => (
                                    <RowComponent key={node.id} row={node} heads={heads} setOpen={setOpen} />
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
}

function TableComponent(props) {

    const { heads, rows, classes } = props;

    const setOpen = (row) => { props.setOpen(row) }

    return (
        <TableContainer component={Paper}>
            <Table className={classes.table}>
                <HeadComponent heads={heads} />
                <TableBody>
                    {rows.map(row => (
                        <RowComponent key={row.id} row={row} heads={heads} setOpen={setOpen} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function GraphicComponent(props) {

    const { chartType, data, height, title } = props;

    const option = {
        title: title,
        chartArea: { width: '40%' },
        isStacked: true,
    };

    return (
        <Chart
            width={'100%'}
            height={height}
            chartType={chartType}
            loader={<div>Loading Chart</div>}
            data={data}
            options={option}
            legendToggle
        />
    )
}

function DateComponent(props) {

    const { field } = props;

    const handleChange = (e) => { props.handleChange(e) }

    return (
        <MuiPickersUtilsProvider locale={ptLocale} utils={DateFnsUtils}>
            <KeyboardDatePicker
                variant="inline"
                format="dd/MM/yyyy"
                margin="normal"
                id="date-picker-inline"
                value={field}
                onChange={(e) => handleChange(e)}
            />
        </MuiPickersUtilsProvider>
    )
}

function SelectComponent(props) {

    const { selectList, selectValue } = props;
    const filter = (e) => { props.filter(e); }

    return (
        <Select
            label=""
            value={selectValue}
            onChange={ (e) => filter(e) }
        >
            {selectList.map(s => (
                <MenuItem key={s.value} value={s.value}> {s.label} </MenuItem>
            ))}
        </Select>
    );
}

export default function FactoryPage() {

    const classes = useStyles();

    const currentYear = new Date().getFullYear();

    const factoryId = getUserFactoryId();

    const [standardApi, setStandardApi] = useState([]);
    const [trainingApi, setTrainingApi] = useState([]);

    const [standards, setStandards] = useState([]);
    const [trainings, setTrainings] = useState([]);
    const [rows, setRows] = useState([]);
    
    const [structures, setStructures] = useState([]);
    const [structureTree, setStructureTree] = useState({});

    const [dateStart, setDateStart] = useState(new Date(currentYear, 0, 1));
    const [dateEnd, setDateEnd] = useState(new Date(currentYear, 11, 31));
    
    const [standardTypeSelect, setStandardTypeSelect] = useState([{label: "Todos", value: 0}]);
    const [structuresSelect, setStructuresSelect] = useState([{label: "Todos", value: 0}]);

    const [standardTypeSelected, setStandardTypeSelected] = useState(0);
    const [structuresSelected, setStructuresSelected] = useState(0);

    const [count, setCount] = useState(0);

    const heads = ["", "Fabrica", "Quantidade"];
    const chartType = "BarChart";

    useEffect(() => {
        const init = async () => {

            const structuresApi = await getStructure(factoryId);

            const structureTreeApi = await getStructureTree();

            const newStandardApi = await getStandard(factoryId);

            const newTrainingApi = await getTraining(factoryId);

            setStructures(structuresApi);

            setStructureTree(structureTreeApi);

            setStandardApi(newStandardApi);

            setTrainingApi(newTrainingApi);
            
            matrix(newStandardApi, newTrainingApi, structureTreeApi, structuresApi);
        }
        init();
    }, []);

    const createRows = (rowsArray, documentArray) => {
        return rowsArray.map(v => ( {id: v.key, cells: [v.label, countDocuments(v.key, documentArray, 0, v.nodes)], nodes: v.nodes ? createRows(v.nodes, documentArray) : null, open: false } ));
    }

    const countDocuments = (id, documentArray, count, nodes) => {

        documentArray.map(v => {
            if (v.structureId === id)
                count += 1;
        });

        nodes.map(v => {
            count += countDocuments(v.key, documentArray, 0, v.nodes);
        });

        return count;
    }

    const setOpen = (row) => {
        row.open = !row.open;
        setCount(count + 1);
    }

    const getStructureName = (structureArray, id) => {
        try {
            return structureArray.filter((x) => x.id===id)[0].name;
        }
        catch(err) {
            console.log(err);
            return id;
        }
    }

    const standardMatrix = (datasetApi, structureArray) => {

        let dataset = [];
        let columns = [];

        datasetApi.map(v => ( columns = createColumns(columns, v.standardType) ));
        datasetApi.map(v => ( dataset = createDataset(dataset, columns, v.structureId, v.standardType, getStructureName(structureArray, v.structureId)) ));

        return [dataset, columns];

    }

    const trainingMatrix = (datasetApi, structureArray) => {

        let dataset = [];
        let columns = [];

        datasetApi.map(v => ( columns = createColumns(columns, v.status ? "Treinados" : "Não treinados") ));
        datasetApi.map(v => ( dataset = createDataset(dataset, columns, v.standard.structureId, v.status ? "Treinados" : "Não treinados", getStructureName(structureArray, v.standard.structureId)) ));

        return [dataset, columns];

    }

    const createColumns = (columns, compareColumns) => {
        let exists = false;

        columns.map(v => {
            if (v === compareColumns)
                exists = true;
        });

        if (!exists)
            columns.push(compareColumns);

        return columns;
    }

    const createDataset = (dataset, columns, compareDataset, compareColumns, labelDataset="") => {
        /* If elements exists in the list so value receive +1 else add new element in the list */

        let exists = false;

        dataset.map(v => {
            if (v[0] === compareDataset) {
                exists = true;

                columns.map((x, index) => {
                    if (x === compareColumns)
                        v[index+2] += 1;
                });
            }
        });

        if (!exists)
            dataset.push([compareDataset, labelDataset ? labelDataset : compareDataset , ...columns.map(v => (
                (v === compareColumns) ? 1 : 0
            ) )]);

        return dataset;
    }

    const updateDateStart = (e) => {
        const value = e;
        setDateStart(e);
        filter(value, dateEnd);
    }

    const updateDateEnd = (e) => {
        const value = e;
        setDateEnd(e);
        filter(dateStart, value);
    }

    const updateStandardType = (e) => {
        console.log(e);
    }

    const updateStructure = (e) => {
        console.log(e);
    }

    const filterDate = (element, start, end) => {
        return new Date(element.updatedAt) >= start && new Date(element.updatedAt) <= end;
    }

    const filter = (start, end) => {

        const newStandardApi = standardApi.filter(x => filterDate(x, start, end));

        const newTrainingApi = trainingApi.filter(x => filterDate(x, start, end));

        matrix(newStandardApi, newTrainingApi, structureTree, structures);
        
    }

    const matrix = (newStandardApi, newTrainingApi, structureTreeApi, structuresApi) => {

        const [datasetStandard, columnsStandard] = standardMatrix(newStandardApi, structuresApi);

        setStandards([['Linha', ...columnsStandard], ...datasetStandard.map(v => ( [v[1], ...columnsStandard.map((x, index) => v[index+2])] ) )]);

        const [datasetTraining, columnsTraining] = trainingMatrix(newTrainingApi, structuresApi);

        setTrainings([['Linha', ...columnsTraining], ...datasetTraining.map(v => ( [v[1], ...columnsTraining.map((x, index) => v[index+2])] ) )]);

        const rowsArray = createRows(structureTreeApi.structures, newStandardApi);

        setRows(rowsArray);
    }

    return(
        <Grid container spacing={2} direction="column">
            <Grid item container spacing={2}>
                <Grid item>
                    <DateComponent field={dateStart} handleChange={updateDateStart} />
                </Grid>
                <Grid item>
                    <DateComponent field={dateEnd} handleChange={updateDateEnd} />
                </Grid>
                <Grid item>
                    <SelectComponent selectList={standardTypeSelect} selectValue={standardTypeSelected } filter={updateStandardType} />
                </Grid>
                <Grid item>
                    <SelectComponent selectList={structuresSelect} selectValue={structuresSelected} filter={updateStructure} />
                </Grid>
            </Grid>
            <Grid item>
                <GraphicComponent data={standards} chartType={chartType} height={standards.length * 40} 
                    title={'Distribuição de documentos por linha'} /> 
            </Grid>
            <Grid item>
                <GraphicComponent data={trainings} chartType={chartType} height={trainings.length * 40} 
                    title={'Treinados x Não treinados por padrões e linhas'} /> 
            </Grid>
            <Grid item>
                <TableComponent rows={rows} heads={heads} classes={classes} setOpen={setOpen} /> 
            </Grid>
        </Grid>
    );
}